import { ReactNode } from 'react'
import moment from 'moment'
import * as Sentry from '@sentry/nextjs'

import { numberToCurrency } from 'utils'

import { IndividualListing } from '../../../services/getListingById'
import { ATTRIBUTION_CONFIGS } from './config'
import { MlsMark } from './MlsMark'

/**
 * MLS attribution + compliance block for the listing detail page (ACR-23).
 * One config-driven block: the identification lines come from the listing
 * payload, lines 3-5 (sourceLine + mark, copyright, disclaimers) come
 * exclusively from ATTRIBUTION_CONFIGS keyed by `feed_id`. No per-feed
 * conditionals live here.
 *
 * Invariants (each verified in MlsAttribution.test.tsx):
 *  - a feed with no config entry renders NO block and reports at error level;
 *  - exactly one MLS mark renders, with no framing text ("Powered by" is
 *    gone — the required courtesy sentence already frames it);
 *  - no line ever renders "undefined" or a bare "Listed by";
 *  - verbatim compliance strings render whole or not at all — a string whose
 *    token cannot be resolved is omitted, never rendered mutated;
 *  - compliance text never rides on an image or its alt text.
 */

const has = (s: string | null | undefined): s is string => !!s?.trim()

/** Config-string substitution slots. Adding one here is the only way a
 * `{Token}` can appear in strings.ts — config.test.ts fails on any token
 * without a resolver. */
export const ATTRIBUTION_TOKENS = ['CurrentYear', 'DataTimestamp'] as const
export type AttributionToken = typeof ATTRIBUTION_TOKENS[number]

const TOKEN_PATTERN = /\{([A-Za-z]+)\}/g
/** Config-string link markup: `[text](url)`. */
export const MARKDOWN_LINK_PATTERN = /\[([^\]]+)\]\(([^\s)]+)\)/

// Dedup for omitted required lines and unconfigured feeds: both are
// non-compliance we must see, but a report per render would drown the alert.
//
// "Once" is scoped to the loaded module instance, NOT a page: these checks run
// in the render body, so they also run during SSR, where the Sets persist for
// the warm Node/Lambda instance across every request it serves. Expect one
// report per key per warm instance — a key already reported stays silent for
// later requests (and later users) on that instance.
const reportedUnresolvedTokens = new Set<string>()
const reportedUnconfiguredFeeds = new Set<string>()

/**
 * Test-only. The dedup Sets are module state, so without a reset every
 * Sentry assertion is order-dependent on which earlier test burned the
 * key (and a passing assertion can be satisfied by the dedup rather than
 * the behavior under test). Fresh-module isolation is NOT an alternative
 * here: it re-instantiates react, and MlsMark's useState then runs under
 * RTL's outer react-dom — "Invalid hook call".
 */
export const __resetAttributionReportDedup = () => {
  reportedUnresolvedTokens.clear()
  reportedUnconfiguredFeeds.clear()
}

/**
 * The backend passes RESO phone strings through with only a trim (five
 * feeds, free-text), so this line receives '+19195551234', '919-555-1234',
 * extensions, anything. Normalize once: format as (NNN) NNN-NNNN only when
 * the input reduces to exactly ten digits (after dropping a leading US '1'
 * from eleven), otherwise render the original string UNCHANGED — on a
 * rule-required contact line (GRID Rule 22 / Triad 18.2 K) an unformatted
 * number is acceptable, a mangled one is not.
 *
 * tel is NULL when the input doesn't reduce to ten digits: concatenating
 * the digits of '919-555-1234 x12' yields a dialable-but-WRONG 12-digit
 * number — invisibly wrong, strictly worse than the visible display
 * mangle this helper exists to prevent. Non-normalizable contacts render
 * as plain text, not an anchor we can't vouch for.
 * Exported for tests.
 */
export const formatPhone = (
  raw: string
): { display: string; tel: string | null } => {
  const digits = raw.replace(/\D/g, '')
  const ten =
    digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
  if (ten.length === 10) {
    return {
      display: `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`,
      tel: `+1${ten}`,
    }
  }
  return { display: raw, tel: null }
}

/**
 * "August 6, 2026 at 5:18 PM EDT" — or null so the caller omits the line.
 * mls_feeds.last_synced is a zone-less UTC timestamp (RDS Data API shape:
 * "YYYY-MM-DD HH:mm:ss"): parse as UTC, then render in Eastern via
 * Intl.DateTimeFormat (built-in IANA tz data; timeZoneName gives DST-correct
 * EDT/EST, never a hardcoded label). MLS GRID verifies the Rule 24 timestamp
 * against reality, so both the parse zone and the display zone must be true.
 * Exported for exact-string tests.
 */
export const formatObtainedAt = (iso: string): string | null => {
  const m = moment.utc(iso)
  if (!m.isValid()) return null
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(m.toDate())
}

type TokenResolvers = Record<AttributionToken, () => string | null>

/**
 * Substitutes every `{Token}` in a config string, or reports the first one
 * that has no value — the caller then omits the whole line rather than
 * rendering a verbatim sentence with a hole in it.
 */
export const resolveTokens = (
  text: string,
  resolvers: TokenResolvers
): { text: string; unresolved: null } | { text: null; unresolved: string } => {
  const values = new Map<string, string>()

  for (const slot of text.match(TOKEN_PATTERN) ?? []) {
    const token = slot.slice(1, -1) as AttributionToken
    const value = resolvers[token]?.() ?? null
    if (value === null) return { text: null, unresolved: token }
    values.set(slot, value)
  }

  return {
    text: text.replace(TOKEN_PATTERN, (slot) => values.get(slot) ?? slot),
    unresolved: null,
  }
}

/**
 * Renders `[text](url)` config markup as real anchors. Split-and-map rather
 * than dangerouslySetInnerHTML: compliance text must never be a path for
 * markup into the page, however Acre-authored the config is.
 */
const renderLine = (text: string): ReactNode[] => {
  const link = new RegExp(MARKDOWN_LINK_PATTERN.source, 'g')
  const nodes: ReactNode[] = []
  let cursor = 0
  let match: RegExpExecArray | null

  while ((match = link.exec(text)) !== null) {
    const [markup, label, href] = match
    if (match.index > cursor) nodes.push(text.slice(cursor, match.index))
    nodes.push(
      <a
        key={`${href}-${match.index}`}
        href={href}
        target="_blank"
        rel="noreferrer"
        className="font-semibold text-teal-3"
      >
        {label}
      </a>
    )
    cursor = match.index + markup.length
  }

  if (cursor < text.length) nodes.push(text.slice(cursor))
  return nodes
}

export function MlsAttribution({
  detailedListing,
}: {
  detailedListing: IndividualListing
}) {
  const { mlsIdx, list_agent } = detailedListing
  const config = ATTRIBUTION_CONFIGS[mlsIdx.feedName]
  const lineClass = 'leading-relaxed text-grey-7'

  if (!config) {
    // A live feed with no config entry is silent non-compliance on every
    // listing in that market, so it is an error, not a warning: the Sentry
    // alert on this message is the guard (ACR-23 req 10).
    if (!reportedUnconfiguredFeeds.has(mlsIdx.feedName)) {
      reportedUnconfiguredFeeds.add(mlsIdx.feedName)
      Sentry.captureMessage(
        `No MLS attribution config for feed_id: ${mlsIdx.feedName}`,
        { level: 'error', tags: { feed: mlsIdx.feedName } }
      )
    }
    return null
  }

  const resolvers: TokenResolvers = {
    CurrentYear: () => String(new Date().getFullYear()),
    DataTimestamp: () =>
      has(mlsIdx.dataObtainedAt)
        ? formatObtainedAt(mlsIdx.dataObtainedAt)
        : null,
  }

  const resolve = (
    text: string,
    // A lost disclaimer degrades the block; a lost SOURCE LINE can erase the
    // feed's entire attribution (GeorgiaMLS has nothing else — mark included,
    // since it nests inside the sourceLine render). That is the same
    // compliance outcome as an unconfigured feed, so it reports at the same
    // severity and rides the same alert rule. NOTE: severity is keyed to the
    // SLOT as a proxy for the outcome — they coincide only because today's
    // strings put {DataTimestamp} solely in GAMLS's sourceLine and the GRID
    // disclaimer; a feed whose erasure came through a non-source line would
    // need this revisited (derive from what actually rendered).
    severity: 'error' | 'warning' = 'warning'
  ): string | null => {
    const { text: resolved, unresolved } = resolveTokens(text, resolvers)
    if (unresolved !== null) {
      // Key EXCLUDES severity: one report per root cause. If the same token
      // ever becomes unresolvable at both a sourceLine and a non-sourceLine
      // site for one feed, the first report wins rather than double-paging
      // for a single null watermark.
      const key = `${mlsIdx.feedName}:${unresolved}`
      if (!reportedUnresolvedTokens.has(key)) {
        reportedUnresolvedTokens.add(key)
        Sentry.captureMessage(
          `Required MLS attribution line omitted: unresolved {${unresolved}} for ${mlsIdx.feedName}`,
          { level: severity, tags: { feed: mlsIdx.feedName } }
        )
      }
      return null
    }
    return resolved
  }

  const agentName = has(list_agent?.full_name) ? list_agent.full_name : null
  const brokerage = has(mlsIdx.brokerage) ? mlsIdx.brokerage : null
  const phone = has(list_agent?.phone_number) ? list_agent.phone_number : null
  const contact = phone !== null ? formatPhone(phone) : null
  const isSold =
    mlsIdx.status?.toLowerCase() === 'closed' ||
    mlsIdx.status?.toLowerCase() === 'sold' ||
    !!mlsIdx.soldDate

  const sourceLine = resolve(config.sourceLine, 'error')
  const copyright = config.copyright ? resolve(config.copyright) : null
  const disclaimers = config.disclaimers
    .map((disclaimer) => ({ key: disclaimer, text: resolve(disclaimer) }))
    .filter((d): d is { key: string; text: string } => d.text !== null)

  return (
    <div className="pt-16">
      {(agentName || brokerage || phone) && (
        <p className={lineClass}>
          {agentName && `Listed by ${agentName}`}

          {/* Contact is REQUIRED (GRID Rule 22 / Triad 18.2 K) even when the
              optional agent name is absent — never gate it on the name. */}
          {contact && (
            <>
              {agentName ? ` ` : ''}
              {contact.tel !== null ? (
                <a
                  href={`tel:${contact.tel}`}
                  className="font-semibold text-teal-3"
                >
                  {contact.display}
                </a>
              ) : (
                <span className="font-semibold text-teal-3">
                  {contact.display}
                </span>
              )}
            </>
          )}

          {brokerage && (agentName || phone ? ` • ${brokerage}` : brokerage)}
          {brokerage && config.officeSuffix}
        </p>
      )}
      {(has(mlsIdx.number) || has(mlsIdx.status)) && (
        <p className={lineClass}>
          {[
            has(mlsIdx.number) ? `Listing #${mlsIdx.number}` : null,
            has(mlsIdx.status) ? mlsIdx.status : null,
          ]
            .filter(Boolean)
            .join(' • ')}
        </p>
      )}
      {mlsIdx.soldDate && mlsIdx.soldPrice && (
        <p className={lineClass}>
          {`Sold: ${moment(mlsIdx.soldDate).format(
            'M/D/YYYY'
          )} for ${numberToCurrency(parseInt(mlsIdx.soldPrice))}`}
        </p>
      )}
      {isSold && config.soldNotice && (
        <p className={lineClass}>{config.soldNotice}</p>
      )}
      <div className="mt-16 flex flex-col gap-10">
        {/* The mark rides inside the source sentence, and only with it: a
            floating logo carries no attribution of its own (req 4/6), and an
            omitted source line means we have nothing to frame it. */}
        {sourceLine && (
          <p className={lineClass}>
            {renderLine(sourceLine)}
            <MlsMark
              config={{ markSrc: mlsIdx.logoUrl }}
              feedName={mlsIdx.feedName}
              // Fixed height, intrinsic width: the per-feed CDN logos differ
              // in aspect ratio, so a fixed box would distort them.
              className="ml-4 inline-block h-[17px] w-auto align-middle"
            />
          </p>
        )}
        {copyright && <p className={lineClass}>{renderLine(copyright)}</p>}
        {disclaimers.map(({ key, text }) => (
          <p key={key} className={lineClass}>
            {renderLine(text)}
          </p>
        ))}
      </div>
    </div>
  )
}
